import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { ModuleTips } from '../components/onboarding';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus, Trash2, Edit, Building, Check, Star, Calendar
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ENTITY_TYPES = [
  { value: 'sole_proprietor', labelKey: 'businesses.sole_proprietor' },
  { value: 'llc', labelKey: 'businesses.llc' },
  { value: 's_corp', labelKey: 'businesses.s_corp' },
  { value: 'c_corp', labelKey: 'businesses.c_corp' },
  { value: 'partnership', labelKey: 'businesses.partnership' }
];

const FILING_STATUS = [
  { value: 'single', labelKey: 'businesses.single' },
  { value: 'married_joint', labelKey: 'businesses.married_joint' },
  { value: 'married_separate', labelKey: 'businesses.married_separate' },
  { value: 'head_household', labelKey: 'businesses.head_household' }
];

const COLORS = [
  '#064e3b', '#0f766e', '#0369a1', '#4338ca', '#7c3aed',
  '#be185d', '#dc2626', '#ea580c', '#ca8a04', '#16a34a'
];

const Businesses = () => {
  const { t } = useTranslation();
  const { getAuthHeaders } = useAuth();
  const [businesses, setBusinesses] = useState([]);
  const [taxCalendar, setTaxCalendar] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmDeleteBusinessId, setConfirmDeleteBusinessId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBusiness, setEditingBusiness] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    entity_type: 'sole_proprietor',
    ein: '',
    owner_name: '',
    filing_status: 'single',
    color: '#064e3b',
    is_default: false
  });

  const currentYear = new Date().getFullYear();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [bizRes, calRes] = await Promise.all([
        axios.get(`${API}/businesses`, { headers: getAuthHeaders(), withCredentials: true }),
        axios.get(`${API}/tax-calendar?year=${currentYear}`, { headers: getAuthHeaders(), withCredentials: true })
      ]);
      
      setBusinesses(bizRes.data);
      setTaxCalendar(calRes.data);
    } catch (error) {
      toast.error(t('businesses.failed_to_load'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingBusiness) {
        await axios.put(`${API}/businesses/${editingBusiness.business_id}`, formData, {
          headers: getAuthHeaders(),
          withCredentials: true
        });
        toast.success(t('businesses.business_updated'));
      } else {
        await axios.post(`${API}/businesses`, formData, {
          headers: getAuthHeaders(),
          withCredentials: true
        });
        toast.success(t('businesses.business_created'));
      }
      
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(t('businesses.failed_to_save'));
    }
  };

  const handleEdit = (business) => {
    setEditingBusiness(business);
    setFormData({
      name: business.name,
      entity_type: business.entity_type,
      ein: business.ein || '',
      owner_name: business.owner_name || '',
      filing_status: business.filing_status || 'single',
      color: business.color || '#064e3b',
      is_default: business.is_default
    });
    setDialogOpen(true);
  };

  const handleDelete = async (businessId) => {
    if (confirmDeleteBusinessId !== businessId) {
      setConfirmDeleteBusinessId(businessId);
      toast.info('Click delete again to confirm', { duration: 3000 });
      setTimeout(() => setConfirmDeleteBusinessId(null), 3000);
      return;
    }
    setConfirmDeleteBusinessId(null);
    
    try {
      await axios.delete(`${API}/businesses/${businessId}`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      toast.success(t('businesses.business_deleted'));
      fetchData();
    } catch (error) {
      toast.error(t('businesses.failed_to_delete'));
    }
  };

  const resetForm = () => {
    setEditingBusiness(null);
    setFormData({
      name: '',
      entity_type: 'sole_proprietor',
      ein: '',
      owner_name: '',
      filing_status: 'single',
      color: '#064e3b',
      is_default: false
    });
  };

  const openNewDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  const isUpcoming = (dateStr) => {
    const date = new Date(dateStr);
    const today = new Date();
    const diffDays = Math.ceil((date - today) / (1000 * 60 * 60 * 24));
    return diffDays >= 0 && diffDays <= 30;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
  <>
    <DashboardLayout>
      <div className="space-y-6" data-testid="businesses-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold mb-1">{t('businesses.title')}</h1>
            <p className="text-muted-foreground">{t('businesses.subtitle')}</p>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button onClick={openNewDialog} data-testid="add-business-btn">
                <Plus className="w-4 h-4 mr-2" />
                {t('businesses.add_new')}
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>{editingBusiness ? t('businesses.edit_business') : t('businesses.add_new_business')}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label>{t('businesses.business_name')}</Label>
                  <Input
                    placeholder="My Business LLC"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                    data-testid="business-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>{t('businesses.entity_type')}</Label>
                  <Select value={formData.entity_type} onValueChange={(v) => setFormData({...formData, entity_type: v})}>
                    <SelectTrigger data-testid="entity-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ENTITY_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>{t(type.labelKey)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('businesses.ein_optional')}</Label>
                    <Input
                      placeholder="XX-XXXXXXX"
                      value={formData.ein}
                      onChange={(e) => setFormData({...formData, ein: e.target.value})}
                      data-testid="ein-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('businesses.owner_name')}</Label>
                    <Input
                      placeholder="John Doe"
                      value={formData.owner_name}
                      onChange={(e) => setFormData({...formData, owner_name: e.target.value})}
                      data-testid="owner-name-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{t('businesses.filing_status')}</Label>
                  <Select value={formData.filing_status} onValueChange={(v) => setFormData({...formData, filing_status: v})}>
                    <SelectTrigger data-testid="filing-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {FILING_STATUS.map(status => (
                        <SelectItem key={status.value} value={status.value}>{t(status.labelKey)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>{t('businesses.color')}</Label>
                  <div className="flex gap-2 flex-wrap">
                    {COLORS.map(color => (
                      <button
                        key={color}
                        type="button"
                        className={`w-8 h-8 rounded-full transition-all ${
                          formData.color === color ? 'ring-2 ring-offset-2 ring-primary' : ''
                        }`}
                        style={{ backgroundColor: color }}
                        onClick={() => setFormData({...formData, color})}
                        data-testid={`color-${color}`}
                      />
                    ))}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="is_default"
                    checked={formData.is_default}
                    onChange={(e) => setFormData({...formData, is_default: e.target.checked})}
                    className="rounded"
                    data-testid="default-checkbox"
                  />
                  <Label htmlFor="is_default">{t('businesses.set_as_default')}</Label>
                </div>

                <Button type="submit" className="w-full" data-testid="submit-business-btn">
                  {editingBusiness ? t('businesses.update_business') : t('businesses.add_new')}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Businesses List */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {businesses.map((business) => (
            <Card 
              key={business.business_id} 
              className="shadow-soft border-border/40 overflow-hidden"
              data-testid={`business-${business.business_id}`}
            >
              <div className="h-2" style={{ backgroundColor: business.color || '#064e3b' }} />
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: `${business.color}20` }}
                    >
                      <Building className="w-5 h-5" style={{ color: business.color }} />
                    </div>
                    <div>
                      <h3 className="font-semibold flex items-center gap-2">
                        {business.name}
                        {business.is_default && (
                          <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                        )}
                      </h3>
                      <p className="text-sm text-muted-foreground capitalize">
                        {business.entity_type?.replace('_', ' ')}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-2 text-sm">
                  {business.ein && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{t('businesses.ein')}</span>
                      <span className="mono">{business.ein}</span>
                    </div>
                  )}
                  {business.owner_name && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{t('businesses.owner')}</span>
                      <span>{business.owner_name}</span>
                    </div>
                  )}
                  {business.filing_status && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{t('businesses.filing_status')}</span>
                      <span className="capitalize">{business.filing_status.replace('_', ' ')}</span>
                    </div>
                  )}
                </div>

                <div className="flex gap-2 mt-4 pt-4 border-t">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => handleEdit(business)}
                    data-testid={`edit-business-${business.business_id}`}
                  >
                    <Edit className="w-4 h-4 mr-1" />
                    {t('common.edit')}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(business.business_id)}
                    className="text-destructive hover:text-destructive"
                    data-testid={`delete-business-${business.business_id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}

          {businesses.length === 0 && (
            <Card className="col-span-full shadow-soft border-border/40">
              <CardContent className="p-12 text-center">
                <Building className="w-16 h-16 mx-auto mb-4 text-muted-foreground/50" />
                <h3 className="text-lg font-medium mb-2">{t('businesses.no_businesses')}</h3>
                <p className="text-muted-foreground mb-4">
                  {t('businesses.no_businesses_desc')}
                </p>
                <Button onClick={openNewDialog}>
                  <Plus className="w-4 h-4 mr-2" />
                  {t('businesses.add_new')}
                </Button>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Tax Calendar */}
        <Card className="shadow-soft border-border/40" data-testid="tax-calendar-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              {t('businesses.tax_calendar')} - {currentYear}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {taxCalendar.length > 0 ? (
              <div className="space-y-3">
                {taxCalendar.map((event) => (
                  <div 
                    key={event.event_id}
                    className={`p-4 rounded-lg border ${
                      event.is_completed 
                        ? 'bg-emerald-50 border-emerald-200' 
                        : isUpcoming(event.due_date)
                          ? 'bg-orange-50 border-orange-200'
                          : 'bg-muted/50 border-border'
                    }`}
                    data-testid={`calendar-event-${event.event_id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {event.is_completed ? (
                          <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center">
                            <Check className="w-4 h-4 text-white" />
                          </div>
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
                            <Calendar className="w-4 h-4 text-white" />
                          </div>
                        )}
                        <div>
                          <p className="font-medium">{event.title}</p>
                          <p className="text-sm text-muted-foreground">{event.description}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <Badge variant={event.is_completed ? 'default' : isUpcoming(event.due_date) ? 'destructive' : 'secondary'}>
                          {formatDate(event.due_date)}
                        </Badge>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-muted-foreground py-8">
                {t('businesses.no_tax_deadlines')}
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
    <ModuleTips moduleId="businesses" />
  </>
  );
};

export default Businesses;
